import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DestroyRef, effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter, skip, take } from 'rxjs';
import { ChannelEvent } from '@app/domain/enums/realtime';
import { CardGameStatus } from '@app/domain/enums/game';
import type { CardGame } from '@app/domain/interfaces/game-card/card-game.interface';
import type { PaginatorResponse } from '@app/domain/interfaces/response/paginator-response.interface';
import { AuthService } from '@app/services/auth/auth';
import { EnvironmentService } from '@app/services/environment/environment.service';
import { PusherConnectionService } from '@app/services/pusher-connection/pusher-connection';

const NO_FLASH_EVENTS = new Set(['match_info', 'first_throw', 'match_ended', 'match_started']);

@Injectable({ providedIn: 'root' })
export class GameNotificationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly httpClient = inject(HttpClient);
  private readonly pusherConnection = inject(PusherConnectionService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly url = `${this.environmentService.apiUrl}/api`;

  private readonly attachedChannels = new Map<number, boolean>();
  private readonly oldGames: Record<number, boolean> = {};
  private readonly needFlashSubject = new BehaviorSubject<boolean>(false);
  private previousUrl = '';

  readonly needFlash = toSignal(this.needFlashSubject, { initialValue: false });

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.loadUserGames();
      } else {
        this.clearState();
      }
    });

    this.pusherConnection.isReady$.pipe(
      filter(Boolean),
      skip(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => { this.reattachAllChannels(); });

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((e: NavigationEnd) => {
      const to = e.urlAfterRedirects.split('?')[0];

      if (this.previousUrl.startsWith('/live-game/')) {
        const gameId = Number(this.previousUrl.split('/')[2]);
        if (!isNaN(gameId) && typeof this.oldGames[gameId] !== 'undefined') {
          this.attachedChannels.set(gameId, false);
          this.attachChannelIfNeeded(gameId);
        }
      }

      if (to.startsWith('/cards') || to.startsWith('/live-game')) {
        this.needFlashSubject.next(false);
      }

      this.previousUrl = to;
    });
  }

  addActiveGame(gameId: number): void {
    if (typeof this.oldGames[gameId] === 'undefined') {
      this.oldGames[gameId] = false;
    }
    this.attachChannelIfNeeded(gameId);
  }

  private reattachAllChannels(): void {
    this.attachedChannels.clear();

    for (const gameIdStr of Object.keys(this.oldGames)) {
      this.attachChannelIfNeeded(Number(gameIdStr));
    }
  }

  private clearState(): void {
    this.attachedChannels.clear();
    this.needFlashSubject.next(false);
  }

  private loadUserGames(): void {
    for (const status of [CardGameStatus.LIVE, CardGameStatus.PURCHASED]) {
      const params = (new HttpParams)
        .set('games_mode', status)
        .set('per_page', '100');

      this.httpClient.get<PaginatorResponse<CardGame>>(`${this.url}/v4/cards/counts`, { params })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(response => {
          const items = response.data.items;

          if (!items.length && status === CardGameStatus.LIVE) {
            this.needFlashSubject.next(false);
          }

          for (const item of items) {
            if (typeof this.oldGames[item.gameId] === 'undefined') {
              this.oldGames[item.gameId] = false;
            }
            this.attachChannelIfNeeded(item.gameId);
          }
        });
    }
  }

  private attachChannelIfNeeded(gameId: number): void {
    if (this.attachedChannels.get(gameId)) return;

    this.attachedChannels.set(gameId, true);

    this.pusherConnection.isReady$.pipe(
      filter(Boolean),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.openGameChannel(gameId));
  }

  private openGameChannel(gameId: number): void {
    try {
      this.pusherConnection.channel(`channel-${gameId}`)
        .listen(ChannelEvent.ALL_EVENT_CREATED, (event: { event: string }) => {
          this.handleGameEvent(gameId, event);
        });
    } catch (err) {
      console.warn('[GameNotificationService] Failed to open channel', gameId, err);
      this.attachedChannels.set(gameId, false);
    }
  }

  private handleGameEvent(gameId: number, event: { event: string }): void {
    const currentPath = this.router.url.split('?')[0];

    if (currentPath.startsWith('/cards') || currentPath.startsWith('/live-game')) {
      return;
    }

    const { event: eventType } = event;

    if (
      typeof this.oldGames[gameId] !== 'undefined' &&
      !this.oldGames[gameId] &&
      !NO_FLASH_EVENTS.has(eventType)
    ) {
      this.oldGames[gameId] = true;
      this.needFlashSubject.next(true);
    }

    if (eventType === 'match_started') {
      this.needFlashSubject.next(true);
    }

    if (eventType === 'game_over') {
      this.needFlashSubject.next(false);
    }
  }
}
